import type { Trade } from "../types/trade";
import type { AccountUpdateData } from "../types/signals";

/** Compare broker account codes (handles `default:DEM_123` vs `DEM_123` style aliases). */
export function brokerAccountCodesMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = a?.trim();
  const right = b?.trim();
  if (!left || !right) return false;
  if (left === right) return true;
  const stripLogicalPrefix = (code: string) => {
    const idx = code.lastIndexOf(":");
    return idx >= 0 ? code.slice(idx + 1) : code;
  };
  return stripLogicalPrefix(left) === stripLogicalPrefix(right);
}

/** True when a row is explicitly tied to a different broker account. */
export function tradeConflictsWithBrokerAccount(
  trade: Pick<Trade, "brokerAccountCode">,
  accountCode: string | null | undefined
): boolean {
  const scopedAccount = accountCode?.trim();
  if (!scopedAccount) return false;
  const tradeAccount = trade.brokerAccountCode?.trim();
  if (!tradeAccount) return false;
  return !brokerAccountCodesMatch(tradeAccount, scopedAccount);
}

/** Drop rows that explicitly belong to another account (API-scoped lists may omit brokerAccountCode). */
export function scopeTradesToBrokerAccount<T extends Pick<Trade, "brokerAccountCode">>(
  trades: T[],
  accountCode: string | null | undefined
): T[] {
  if (!accountCode?.trim()) return trades;
  return trades.filter((trade) => !tradeConflictsWithBrokerAccount(trade, accountCode));
}

/** Merge supplemental rows into a list by trade id (later rows win). */
export function mergeTradesById<T extends Pick<Trade, "id">>(
  primary: T[],
  supplemental: T[]
): T[] {
  if (supplemental.length === 0) return primary;
  const byId = new Map(primary.map((trade) => [trade.id, trade]));
  for (const trade of supplemental) {
    byId.set(trade.id, trade);
  }
  return Array.from(byId.values());
}

/** Open trade UUIDs from broker snapshot / WebSocket account metrics for the active account. */
export function resolveOpenTradeIdsFromAccountMetrics(
  accountMetrics: Record<string, AccountUpdateData> | null | undefined,
  accountCode: string | null | undefined
): string[] {
  if (!accountMetrics || !accountCode?.trim()) return [];
  const scoped = accountCode.trim();

  const direct = accountMetrics[scoped]?.open_trade_ids;
  if (Array.isArray(direct) && direct.length > 0) {
    return Array.from(new Set(direct.filter(Boolean)));
  }

  for (const [key, data] of Object.entries(accountMetrics)) {
    if (key === "__latest") continue;
    const code = data.account_code || key;
    if (!brokerAccountCodesMatch(code, scoped)) continue;
    if (Array.isArray(data.open_trade_ids) && data.open_trade_ids.length > 0) {
      return Array.from(new Set(data.open_trade_ids.filter(Boolean)));
    }
  }

  const latest = accountMetrics.__latest;
  if (
    latest &&
    brokerAccountCodesMatch(latest.account_code, scoped) &&
    Array.isArray(latest.open_trade_ids) &&
    latest.open_trade_ids.length > 0
  ) {
    return Array.from(new Set(latest.open_trade_ids.filter(Boolean)));
  }

  return [];
}
